import React, { useEffect, useRef, useState } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import ActionDropdownItem from "../../components/Common/PageTitle/ActionDropdownItem";
import ActivityLoader from "../../components/Common/ActivityLoader";
import Notification from "../../components/Common/Notification";
import functions from "../../util/functions";
import { useLedger } from "../../DataStore/LedgerContext";
import Pagination from "../../components/Common/Pagination";
import { useTheme } from "../../DataStore/ThemeContext";
import AlertNotification from "../../components/Common/AlertNotification";
import AlertDeleteLedger from "../../components/Ledger/AlertDeleteLedger";
import AlertDateSelector from "../../components/Report/AlertDateSelector";
import printContent, { formatDate, numberFormat } from "../../util/araknerdlib";
import LedgerFilterBar from "../../components/Report/LedgerFilterBar";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Link } from "react-router-dom";

const LedgerPage = () => {
  const { ledgerList, getTransactions, setCurrentPage, metaData, refresh, dispatch, fetchedPeriod, mounted } = useLedger();
  const [selected, setSelected] = useState(null);
  const { setNotify } = useTheme();
  const [showDelete, toggleDelete] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const statementRef = useRef(); // Ref to enclose statement content for printing
  

  useEffect(() => {
     if(!mounted){
         getTransactions();
     }
  }, []);

  const handleDelete = ()=>{
    if(!selected){
        setNotify(<AlertNotification type="info" message="No transaction selected. Please make selection then try again." iconClass="fa fa-info-circle"/>);
        return;
    }
    toggleDelete(true);
  }

   const handleConfirmDateRange = async (range) => {
        dispatch({ type: 'dateRange', payload: range });
        setCurrentPage(1);
        await getTransactions(true, range);
      };

  const exportLedgerToExcel = (rows = []) => {
      if (!Array.isArray(rows) || rows.length === 0) {
        setNotify(
          <AlertNotification
            type="info"
            message="No ledger entries to export."
            iconClass="fa fa-info-circle"
          />
        );
        return;
      }

      const data = rows.map((t, idx) => ({
        "#": idx + 1,
        "Record Date": functions.formatDate(t.created_at),
        "Trans Date": functions.formatDate(t.transaction_date),
        "L/F": t.ledger_no || "",
        "Payee": t?.payee?.title || "-",
        "Amount (UGX)": Number(t.amount || 0),
        "Description": t?.description || "",
        "Account": t?.account?.account_name || "",
        "Tag": t?.tag?.tag_name || "-",
        "Username": t?.created_by?.username || "",
      }));

      const ws = XLSX.utils.json_to_sheet(data);

      // Appropriate column widths
      ws["!cols"] = [
        { wch: 4 },   // #
        { wch: 12 },  // Record Date
        { wch: 12 },  // Trans Date
        { wch: 12 },  // L/F
        { wch: 18 },  // Payee
        { wch: 14 },  // Amount
        { wch: 30 },  // Description
        { wch: 22 },  // Account
        { wch: 14 },  // Tag
        { wch: 14 },  // Username
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Ledger Entries");

      const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });

      const from = fetchedPeriod?.opening_date
        ? functions.formatDate(fetchedPeriod.opening_date)
        : "";
      const to = fetchedPeriod?.closing_date
        ? functions.formatDate(fetchedPeriod.closing_date)
        : "";

      const today = new Date().toISOString().slice(0, 10);
      const periodPart = from && to ? `${from}_to_${to}` : "All";
      const filename = `Ledger_Entries_${periodPart}_${today}.xlsx`.replace(/[\/\\:*?"<>|]/g, "-");

      saveAs(new Blob([out], { type: "application/octet-stream" }), filename);
  };



  const PageActions = () => (
    <>
      <ActionDropdownItem iconClass="fa fa-calendar-alt text-success" label="Filter Date Range" onClick={()=>setShowAlert(true)}/>
      <ActionDropdownItem
        iconClass="fa fa-file-excel text-primary"
        label="Export Excel"
        onClick={() => exportLedgerToExcel(ledgerList)}
      />
      <ActionDropdownItem iconClass="fa fa-trash-alt" label="Delete Entry" onClick={handleDelete} className="text-danger"/>
      <ActionDropdownItem
        iconClass="fa fa-sync"
        label="Refresh"
        onClick={refresh}
      />
      <ActionDropdownItem iconClass="fa fa-print text-secondary" label="Print Entries" onClick={()=>printContent(statementRef, "Ledger Entries")}/>

    </>
  );

  return (
    <>
    <PageContainer>
      <MainContent>
        <PageTitle
          title="Ledger Entries"
          description={`Showing all ledger entries for the selected financial period ${fetchedPeriod ? `(${ functions.formatDate(fetchedPeriod.opening_date)} to ${functions.formatDate(fetchedPeriod.closing_date)})` : ''}`}
          iconClass="fa fa-exchange-alt"
          PageActions={PageActions}
          sticky={true}
          Pagination={
            metaData ? (
              <Pagination
                metaData={metaData}
                onPageChange={(page) => setCurrentPage(page)}
              />
            ) : null
          }
        />
        <LedgerFilterBar filter={handleConfirmDateRange} handleExport={() => exportLedgerToExcel(ledgerList)} />
        <div className="card mb-3">
          <div className="card-body">
            {Array.isArray(ledgerList) && 
              <div className="table-responsive" ref={statementRef}>
                <table className="table table-hover table-striped table-bordered">
                  {/* <title>Ledger Entries</title> */}
                  <caption>{`Ledger entries for the selected financial period ${fetchedPeriod ? `(${ functions.formatDate(fetchedPeriod.opening_date)} to ${functions.formatDate(fetchedPeriod.closing_date)})` : ''}`}</caption>
                  <thead>
                    <tr>
                      <th className="nowrap">Record Date</th>
                      <th className="nowrap">Trans Date</th>
                      <th>L/F</th>
                      <th>Payee</th>
                      <th>Amount</th>
                      <th>Description</th>
                      <th>Account</th>
                      <th>Tag</th>
                      <th>Username</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerList.map((transaction, index) => (
                      <tr
                        key={index}
                        className={`link ${
                          transaction.core_grouping_id ===
                          selected?.core_grouping_id
                            ? "selected-row"
                            : ""
                        }`}
                        onClick={() => setSelected(transaction)}
                      >
                        <td className="nowrap">
                          {formatDate(transaction.created_at)}
                        </td>
                        <td>
                          {formatDate(transaction.transaction_date)}
                        </td>
                        <td>
                          <Link to={`/transaction-ledger/${transaction.ledger_no}`}>
                            {transaction.ledger_no}
                          </Link>
                        </td>
                        <td>{transaction.payee?.title || "-"}</td>
                        <td>{numberFormat(transaction.amount)}</td>
                        <td>{transaction.description}</td>
                        <td>{transaction.account.account_name}</td>
                        <td>{transaction.tag?.tag_name || "-"}</td>
                        <td>{transaction.created_by.username}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            }

            {ledgerList === "404" &&
              <Notification
                type="info"
                message="No transactions found for this financial period."
                closable={false}
              />}
            {!ledgerList && <ActivityLoader width={20} />}
          </div>
        </div>
      </MainContent>
    </PageContainer>
        
    {selected && 
            <AlertDeleteLedger
            title="Delete Ledger Entry"
            alertDescription={`Please confirm that you want to delete the selected ledger entry: ${selected.ledger_no}. This action is irreversible.`}
            show={showDelete}
            handleClose={() => toggleDelete(false)}
            selection={[selected.core_grouping_id]}
            notes="All records associated with this ledger entry will be deleted."
          />
          }


      <AlertDateSelector
            show={showAlert}
            onClose={() => setShowAlert(false)}
            onConfirm={handleConfirmDateRange}
        />

    </>

  );
};

export default LedgerPage;
